import React, { useEffect, useState } from 'react';
import { resumeAudioContext } from './audio/audioManager';
import { useTableStore } from './state/tableStore';

type PokerTable = {
  id: string;
  type: string;
  stakes: string;
  players: string;
  status: string;
  private: boolean;
};

type UserInfo = {
  displayName: string;
  avatar: string;
  avatarUrl: string | null;
  balance: number;
  loggedIn: boolean;
};

type LobbyProps = {
  onStart: (info: { tableId?: string; buyIn?: number; autoSit?: boolean }) => void;
};

export const Lobby: React.FC<LobbyProps> = ({ onStart }) => {
  const { setAccountBalance } = useTableStore();
  const [tables, setTables] = useState<PokerTable[]>([]);
  const [user, setUser] = useState<UserInfo | null>(null);
  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const [selectedLevel, setSelectedLevel] = useState<'low' | 'medium' | 'high'>('low');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);
        const [tablesRes, userRes] = await Promise.all([
          fetch('/api/poker/tables', { credentials: 'include' }),
          fetch('/api/poker/userinfo', { credentials: 'include' }),
        ]);
        const tablesJson = tablesRes.ok ? await tablesRes.json() : [];
        const userJson = userRes.ok ? await userRes.json() : null;
        if (!mounted) return;
        setTables(Array.isArray(tablesJson) ? tablesJson : []);
        setUser(userJson);
        if (userJson && typeof userJson.balance === 'number') {
          setAccountBalance(userJson.balance);
        }
        setSelectedTable(Array.isArray(tablesJson) && tablesJson.length > 0 ? tablesJson[0].id : null);
      } catch (err) {
        if (!mounted) return;
        setError('Unable to load poker lobby. Please refresh.');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [setAccountBalance]);

  const getLevelDefaults = (level: 'low' | 'medium' | 'high') => {
    switch (level) {
      case 'medium':
        return { stake: 100000, smallBlind: 500 };
      case 'high':
        return { stake: 1000000, smallBlind: 5000 };
      default:
        return { stake: 10000, smallBlind: 50 };
    }
  };

  const resolveLevelFromType = (type?: string | null) => {
    const normalized = (type ?? '').toLowerCase();
    if (normalized.includes('high')) return 'high';
    if (normalized.includes('medium')) return 'medium';
    return 'low';
  };

  const withEndSuffix = (value: string) => (value.toLowerCase().endsWith(' end') ? value : `${value} end`);

  const handleCreate = async (level: 'low' | 'medium' | 'high') => {
    try {
      await resumeAudioContext();
      setCreating(true);
      const res = await fetch('/api/poker/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ private: 'false', level }),
        credentials: 'include',
      });
      if (!res.ok) {
        setError('Failed to create table. Please sign in and try again.');
        return;
      }
      const data = await res.json();
      const newId = data?.id || data?.gameId || data?.tableId;
      const defaults = getLevelDefaults(level);
      const buyIn = Number(data?.stake ?? defaults.stake);
      if (user?.loggedIn && typeof user.balance === 'number' && user.balance < buyIn) {
        setError(`Insufficient balance. You need ${buyIn} CIB to join this table.`);
        return;
      }
      if (newId) {
        onStart({ tableId: newId, buyIn });
      } else {
        onStart({});
      }
    } catch (err) {
      setError('Failed to create table. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const handleJoin = async () => {
    try {
      await resumeAudioContext();
      setCreating(true);
      const res = await fetch('/api/poker/join', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ tableId: selectedTable ?? '' }),
        credentials: 'include',
      });
      if (!res.ok) {
        setError('Failed to join table. Please sign in and try again.');
        return;
      }
      const data = await res.json();
  const joinId = data?.tableId || selectedTable || '1';
  const selected = tables.find((table) => table.id === joinId);
  const level = resolveLevelFromType(selected?.type);
  const defaults = getLevelDefaults(level);
  const buyIn = Number(data?.stake ?? defaults.stake);
      if (user?.loggedIn && typeof user.balance === 'number' && user.balance < buyIn) {
        setError(`Insufficient balance. You need ${buyIn} CIB to join this table.`);
        return;
      }
      onStart({ tableId: joinId, buyIn });
    } catch (err) {
      setError('Failed to join table. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        background: 'radial-gradient(circle at top, #1E293B, #0F172A)',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 5000,
      }}
    >
      <div style={{ width: 'min(960px, 92vw)', padding: 32, background: 'rgba(15,23,42,0.85)', borderRadius: 20, boxShadow: '0 24px 80px rgba(0,0,0,0.4)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 30 }}>Poker Lobby</h1>
            <p style={{ margin: '8px 0 0', color: '#cbd5f5' }}>Choose a table or create one to start playing.</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {user?.avatarUrl ? (
              <img src={user.avatarUrl} alt="avatar" style={{ width: 44, height: 44, borderRadius: '50%' }} />
            ) : (
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#334155', display: 'grid', placeItems: 'center', fontWeight: 700 }}>
                {user?.avatar ?? 'G'}
              </div>
            )}
            <div>
              <div style={{ fontWeight: 700 }}>{user?.displayName ?? 'Guest'}</div>
              <div style={{ fontSize: 12, color: '#94a3b8' }}>{user?.loggedIn ? `${user?.balance ?? 0} CIB` : 'Not signed in'}</div>
            </div>
            {!user?.loggedIn && (
              <a href="/oauth2/authorization/discord" style={{ color: '#38bdf8', fontWeight: 700 }}>
                Sign in
              </a>
            )}
          </div>
        </div>
        {error && <div style={{ background: '#b91c1c', padding: 12, borderRadius: 12, marginBottom: 16 }}>{error}</div>}
        {loading ? (
          <div style={{ padding: 20, color: '#cbd5f5' }}>Loading tables...</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 20 }}>
            <div style={{ background: '#0b1324', padding: 16, borderRadius: 14 }}>
              <div style={{ fontWeight: 700, marginBottom: 12 }}>Available Tables</div>
              {tables.length === 0 ? (
                <div style={{ color: '#94a3b8' }}>No tables available. Create one to start.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 360, overflowY: 'auto' }}>
                  {tables.map((table) => (
                    <label
                      key={table.id}
                      style={{
                        display: 'flex',
                        gap: 12,
                        alignItems: 'center',
                        padding: 12,
                        borderRadius: 12,
                        background: selectedTable === table.id ? '#1f2a44' : '#0f172a',
                        border: '1px solid #1f2a44',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="radio"
                        name="table"
                        checked={selectedTable === table.id}
                        onChange={() => setSelectedTable(table.id)}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600 }}>{withEndSuffix(table.type)}</div>
                        <div style={{ fontSize: 12, color: '#94a3b8' }}>{table.stakes} • {table.players} • {table.status}</div>
                      </div>
                      {table.private && <span style={{ fontSize: 12, color: '#facc15' }}>Private</span>}
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div style={{ background: '#0b1324', padding: 16, borderRadius: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontWeight: 700 }}>Lobby Actions</div>
              <button
                onClick={handleJoin}
                style={{ padding: '12px 16px', borderRadius: 12, border: 'none', background: '#22c55e', color: '#0f172a', fontWeight: 700 }}
              >
                Join Table
              </button>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>Create table level</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {(['low', 'medium', 'high'] as const).map((level) => (
                    <button
                      key={level}
                      onClick={() => setSelectedLevel(level)}
                      style={{
                        padding: '8px 12px',
                        borderRadius: 10,
                        border: '1px solid #334155',
                        background: selectedLevel === level ? '#1f2a44' : 'transparent',
                        color: '#e2e8f0',
                        fontWeight: 700,
                        textTransform: 'capitalize',
                      }}
                    >
                      {level}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => handleCreate(selectedLevel)}
                  disabled={creating}
                  style={{ padding: '12px 16px', borderRadius: 12, border: 'none', background: '#38bdf8', color: '#0f172a', fontWeight: 700, opacity: creating ? 0.7 : 1 }}
                >
                  {creating ? 'Creating…' : `Create ${selectedLevel} Table`}
                </button>
              </div>
                <button
                onClick={async () => {
                  await resumeAudioContext();
                  onStart({ autoSit: false });
                }}
                style={{ padding: '12px 16px', borderRadius: 12, border: '1px solid #334155', background: 'transparent', color: '#e2e8f0', fontWeight: 700 }}
              >
                Enter as Spectator
              </button>
              <div style={{ fontSize: 12, color: '#94a3b8' }}>
                The lobby connects to <code>/api/poker/tables</code> and <code>/api/poker/create</code>.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
